import { FormControl } from "@angular/forms";
import { FormGroup } from "@angular/forms";
import { CustomErrorType, ErrorMesage, Operator, RangeOperation } from "../model/validator.model";
import { StringUtil } from "../utility/string.utils";

export class CustomValidator {
  static blank(message: string = "This field is mandatory.") {
    return (control: FormControl): ErrorMesage => {
      const val = control.value;
      if (StringUtil.isBlank(val)) {
        return new ErrorMesage(CustomErrorType.BLANK, message);
      }
      return null;
    };
  }

  static blankOnAnotherControl(
    anotherControl: FormControl,
    message: string = "This field is mandatory."
  ) {
    return (control: FormControl): ErrorMesage => {
      const val = anotherControl.value;
      if (StringUtil.isBlank(val)) {
        return new ErrorMesage(CustomErrorType.BLANK, message);
      }
      return null;
    };
  }

  static required(control: FormControl, max?: number): ErrorMesage {
    const msg = "This field is required.";
    const value = control.value;
    if (value === null || value === undefined || value.length === 0) {
      return new ErrorMesage(CustomErrorType.REQUIRED, msg);
    } else if (typeof value === "number") {
      return null;
    } else if (typeof value === "string" && value.trim().length === 0) {
      return new ErrorMesage(CustomErrorType.REQUIRED, msg);
    } else {
      return null;
    }
  }

  static maxLength(max?: number) {
    return (control: FormControl): ErrorMesage => {
      if (`${control.value}`.length > max) {
        return new ErrorMesage(CustomErrorType.MAX_LENGTH, `Maximum length is ${max} characters.`);
      } else {
        return null;
      }
    };
  }

  static range(min: number, max: number) {
    return (control: FormControl): ErrorMesage => {
      if (control.value !== null && !isNaN(control.value)) {
        const num = parseInt(control.value, 10);
        if (num < min || num > max) {
          return new ErrorMesage(
            CustomErrorType.RANGE,
            `Input value must be in range from ${min} to ${max}.`
          );
        }
        return null;
      } else {
        return null;
      }
    };
  }

  static compare(source: string, targetControlName: string, op: Operator) {
    return (form: FormGroup): any => {
      const sourceVal = parseFloat(form.value[source]);
      const targetVal = parseFloat(form.value[targetControlName]);
      const targetControl = form.controls[source];

      if (sourceVal !== null && targetVal !== null && !isNaN(sourceVal) && !isNaN(targetVal)) {
        let isValid = true;
        switch (op.key) {
          case RangeOperation.GT.key: {
            isValid = sourceVal > targetVal;
            break;
          }
          case RangeOperation.LT.key: {
            isValid = sourceVal < targetVal;
            break;
          }
        }

        if (isValid) {
          if (targetControl.errors && targetControl.errors.type === CustomErrorType.COMPARE) {
            targetControl.setErrors(null);
            targetControl.updateValueAndValidity();
          }
          return form.errors;
        } else {
          targetControl.setErrors(
            new ErrorMesage(
              CustomErrorType.COMPARE,
              `Input value must be ${op.display} ${targetVal}.`
            )
          );
          return form.errors;
        }
      } else {
        return form.errors;
      }
    };
  }

  static unique(field: string) {
    return (control: FormControl): any => {
      if (
        !control.value ||
        typeof control.value !== "string" ||
        control.value.trim().length === 0
      ) {
        return null;
      }
      const duplicatedErr = new ErrorMesage(CustomErrorType.UNIQUE, `This field is duplicated.`);
      const currentVal: string = control.value;
      const group = control.parent;
      let isDuplicated = false;

      // check all validates
      Object.keys(group.controls).forEach(key => {
        if (key !== field) {
          const crt = group.get(key);
          if ((crt.value as string).trim().toLowerCase() === currentVal.trim().toLowerCase()) {
            isDuplicated = true;
            return;
          }
        }
      });

      if (isDuplicated) {
        control.setErrors(duplicatedErr);
        return control.errors;
      }

      return null;
    };
  }

  static positiveNumber(control: FormControl, max?: number): ErrorMesage {
    const msg = "Wrong data format. Number only";
    const value = control.value;
    if (value !== null) {
      if (`${value}`.match(/^-?\d*(\.\d+)?$/)) {
        if (parseFloat(value) < 0) {
          return new ErrorMesage(CustomErrorType.POSITIVE_NUMBER, msg);
        } else {
          return null;
        }
      } else {
        return new ErrorMesage(CustomErrorType.POSITIVE_NUMBER, msg);
      }
    }
  }

  static rangeControl(source: string, minControlName: string, maxControlName: string) {
    return (form: FormGroup): any => {
      const sourceVal = parseFloat(form.value[source]);
      const min = parseFloat(form.value[minControlName]);
      const max = parseFloat(form.value[maxControlName]);
      const targetControl = form.controls[source];

      if (sourceVal !== null && !isNaN(min) && !isNaN(max) && min < max) {
        if (sourceVal >= min && sourceVal <= max) {
          // Valid
          if (targetControl.errors && targetControl.errors.type === CustomErrorType.RANGE_CONTROL) {
            targetControl.setErrors(null);
            targetControl.updateValueAndValidity();
          }
        } else {
          targetControl.setErrors(null);
          targetControl.setErrors(
            new ErrorMesage(
              CustomErrorType.RANGE_CONTROL,
              `Input value must be in range from ${min} to ${max}.`
            )
          );
        }
      } else {
        targetControl.setErrors(null);
      }

      return form.errors;
    };
  }

  static email(control: FormControl): ErrorMesage {
    const msg = "Wrong email format";
    const value = control.value;
    if (value === null) {
      return null;
    } else {
      if (`${value}`.match(/^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/)) {
        return null;
      } else {
        return new ErrorMesage(CustomErrorType.EMAIL, msg);
      }
    }
  }

  static stringName(control: FormControl): ErrorMesage {
    const msg = "Wrong data format. String only ";
    const value = control.value;
    if (value === null || `${value}`.match(/^[\sa-zA-ZåäöÅÄÖ&(),.'"-]*$/)) {
      return null;
    } else {
      return new ErrorMesage(CustomErrorType.STRING, msg);
    }
  }

  static url(control: FormControl): ErrorMesage {
    const msg = "Wrong url format";
    const value = control.value;
    if (value === null) {
      return null;
    } else {
      if (
        `${value}`.match(
          /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_\+.~#?&=]*)/
        )
      ) {
        return null;
      } else {
        return new ErrorMesage(CustomErrorType.URL, msg);
      }
    }
  }

  static notEqual(
    control1: FormControl,
    control2: FormControl,
    message: string = "These two fields can not have the same value"
  ) {
    return (control: FormGroup): ErrorMesage => {
      if (!control1.value || !control2.value) {
        return null;
      }
      if (control1.value === control2.value) {
        return new ErrorMesage(CustomErrorType.NOT_EQUAL, message);
      }
      return null;
    };
  }
}
